<template>
  <div class="ai-history-page">

    <!-- Page Header -->
    <div class="page-header mb-4">
      <div class="header-left">
        <h2 class="page-title">
          <span class="page-icon">🗂️</span>
          AI Analysis History
        </h2>
        <p class="page-subtitle text-muted mb-0">Browse, search, and review all previous AI-generated reports</p>
      </div>
      <button class="btn btn-primary btn-sm" @click="$router.push('/')">
        ➕ New Analysis
      </button>
    </div>

    <div class="history-layout">

      <!-- ─── LEFT: Filter Panel ─── -->
      <aside class="filter-panel">
        <div class="filter-card">
          <h6 class="filter-title">🔍 Filter Results</h6>

          <div class="filter-group">
            <label class="filter-label">Analysis Type</label>
            <select v-model="filters.promptType" class="form-select form-select-sm">
              <option value="">All Types</option>
              <option v-for="p in prompts" :key="p.key" :value="p.key">{{ p.label }}</option>
            </select>
          </div>

          <div class="filter-group">
            <label class="filter-label">From Date</label>
            <input v-model="filters.dateFrom" type="date" class="form-control form-control-sm" />
          </div>

          <div class="filter-group">
            <label class="filter-label">To Date</label>
            <input v-model="filters.dateTo" type="date" class="form-control form-control-sm" />
          </div>

          <div class="filter-group">
            <label class="filter-label">Requested By</label>
            <input
              v-model="filters.userSearch"
              type="text"
              class="form-control form-control-sm"
              placeholder="Username..."
              @keyup.enter="applyFilters"
            />
          </div>

          <div class="filter-actions">
            <button class="btn btn-primary btn-sm w-100 mb-2" @click="applyFilters">
              Apply Filters
            </button>
            <button class="btn btn-outline-secondary btn-sm w-100" @click="clearFilters">
              Clear All
            </button>
          </div>

          <!-- Active filter tags -->
          <div v-if="hasActiveFilters" class="active-filters mt-3">
            <div class="filter-tag-label">Active:</div>
            <span v-if="filters.promptType" class="filter-tag">
              {{ getPromptLabel(filters.promptType) }}
              <button @click="filters.promptType = ''; applyFilters()">×</button>
            </span>
            <span v-if="filters.dateFrom" class="filter-tag">
              From: {{ filters.dateFrom }}
              <button @click="filters.dateFrom = ''; applyFilters()">×</button>
            </span>
            <span v-if="filters.dateTo" class="filter-tag">
              To: {{ filters.dateTo }}
              <button @click="filters.dateTo = ''; applyFilters()">×</button>
            </span>
            <span v-if="filters.userSearch" class="filter-tag">
              User: {{ filters.userSearch }}
              <button @click="filters.userSearch = ''; applyFilters()">×</button>
            </span>
          </div>
        </div>

        <!-- Stats box -->
        <div class="stats-card mt-3">
          <div class="stat-item">
            <span class="stat-number">{{ totalCount }}</span>
            <span class="stat-label">Total Records</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ totalPages }}</span>
            <span class="stat-label">Pages</span>
          </div>
        </div>
      </aside>

      <!-- ─── RIGHT: Results ─── -->
      <main class="results-panel">

        <!-- Detail view -->
        <transition name="slide-in">
          <div v-if="selectedItem" class="detail-card">
            <div class="detail-header">
              <div class="detail-header-left">
                <button class="btn btn-sm btn-outline-secondary me-2" @click="selectedItem = null">
                  ← Back to List
                </button>
                <span class="badge prompt-badge" :class="getBadgeClass(selectedItem.promptType)">
                  {{ getPromptLabel(selectedItem.promptType) }}
                </span>
                <span class="detail-meta">
                  {{ formatDate(selectedItem.created_at) }} &bull;
                  {{ selectedItem.created_by_name || 'System' }}
                </span>
              </div>
              <div class="detail-actions">
                <button class="btn btn-sm btn-outline-secondary me-2" @click="printItem">
                  🖨️ Print
                </button>
                <button class="btn btn-sm btn-link" @click="showSnapshot = !showSnapshot">
                  {{ showSnapshot ? 'Hide' : 'Show' }} Raw Data
                </button>
              </div>
            </div>

            <div class="detail-body answer-body" v-html="renderedMarkdown"></div>

            <transition name="fade">
              <pre v-if="showSnapshot" class="raw-data">{{ selectedItem.snapshot }}</pre>
            </transition>
          </div>
        </transition>

        <!-- Table view -->
        <div v-if="!selectedItem">

          <!-- Loading -->
          <div v-if="loading" class="loading-state">
            <div class="spinner-grow text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading history…</p>
          </div>

          <!-- Empty -->
          <div v-else-if="items.length === 0" class="empty-state">
            <div class="empty-icon">📭</div>
            <h5>No records found</h5>
            <p class="text-muted">
              {{ hasActiveFilters ? 'No results match your filters. Try clearing them.' : 'No AI analyses have been generated yet.' }}
            </p>
            <button v-if="hasActiveFilters" class="btn btn-outline-primary btn-sm" @click="clearFilters">
              Clear Filters
            </button>
          </div>

          <!-- Results table -->
          <div v-else>
            <div class="results-header mb-2">
              <span class="results-count">{{ totalCount }} result{{ totalCount !== 1 ? 's' : '' }}</span>
            </div>
            <div class="table-card">
              <table class="history-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date &amp; Time</th>
                    <th>Analysis Type</th>
                    <th>Requested By</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, idx) in items"
                    :key="item.id"
                    class="table-row"
                    @click="openItem(item)"
                  >
                    <td class="row-num">{{ (currentPage - 1) * pageSize + idx + 1 }}</td>
                    <td class="row-date">{{ formatDate(item.created_at) }}</td>
                    <td>
                      <span class="badge prompt-badge" :class="getBadgeClass(item.promptType)">
                        {{ getPromptLabel(item.promptType) }}
                      </span>
                    </td>
                    <td class="row-user">
                      <span class="user-chip">
                        <i class="bi bi-person-fill"></i>
                        {{ item.created_by_name || 'System' }}
                      </span>
                    </td>
                    <td>
                      <button class="btn btn-sm btn-view" @click.stop="openItem(item)">
                        View Report
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="pagination-bar mt-3" v-if="totalPages > 1">
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="currentPage === 1"
                @click="goPage(currentPage - 1)"
              >
                ← Prev
              </button>
              <div class="page-numbers">
                <button
                  v-for="p in visiblePages"
                  :key="p"
                  class="page-btn"
                  :class="{ active: p === currentPage }"
                  @click="goPage(p)"
                >
                  {{ p }}
                </button>
              </div>
              <button
                class="btn btn-sm btn-outline-secondary"
                :disabled="currentPage === totalPages"
                @click="goPage(currentPage + 1)"
              >
                Next →
              </button>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { marked } from 'marked'
import api from '@/api/axios'

/* ── Prompts list (same as DashboardAi) ── */
const prompts = [
  { key: 'ethiopian-govt-report',  label: '🇪🇹 Ethiopian Government Report' },
  { key: 'govt-audit-ethiopia',    label: '🔎 Government Audit (Ethiopia)' },
  { key: 'forecast-4-weeks',       label: '4-Week Sales Forecast' },
  { key: 'safety-stock',           label: 'Safety Stock Advice' },
  { key: 'abc-analysis',           label: 'ABC Sales Classification' },
  { key: 'dead-stock',             label: 'Dead Stock (>90 Days)' },
  { key: 'supplier-risk',          label: 'Supplier Delay Ranking' },
  { key: 'promo-uplift',           label: 'Promotion Uplift Analysis' },
  { key: 'eoq-compare',            label: 'EOQ vs AI Order Size' },
  { key: 'shelf-camera-audit',     label: 'Shelf Photo Audit (Missing/Extra)' },
  { key: 'supplier-risk-map',      label: 'Supplier Risk Heat-Map' },
  { key: 'cashflow-simulation',    label: 'Inventory Cashflow Simulation' },
  { key: 'model-benchmark',        label: 'Forecast Model Winner' },
  { key: 'auto-purchase-orders',   label: 'Auto Purchase Order Suggestion' },
]

/* ── Filter state ── */
const filters = ref({ promptType: '', dateFrom: '', dateTo: '', userSearch: '' })

/* ── Table state ── */
const items       = ref([])
const loading     = ref(false)
const currentPage = ref(1)
const pageSize    = ref(15)
const totalCount  = ref(0)

/* ── Detail state ── */
const selectedItem = ref(null)
const showSnapshot = ref(false)

/* ── Computed ── */
const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / pageSize.value)))

const hasActiveFilters = computed(() =>
  !!(filters.value.promptType || filters.value.dateFrom || filters.value.dateTo || filters.value.userSearch)
)

const renderedMarkdown = computed(() => {
  if (!selectedItem.value?.result) return ''
  return marked(selectedItem.value.result)
})

const visiblePages = computed(() => {
  const total = totalPages.value
  const cur   = currentPage.value
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages = new Set([1, total, cur, cur - 1, cur + 1].filter(p => p >= 1 && p <= total))
  return [...pages].sort((a, b) => a - b)
})

/* ── Methods ── */
async function fetchHistory() {
  loading.value = true
  try {
    const params = { page: currentPage.value, page_size: pageSize.value }
    if (filters.value.promptType) params.prompt_type = filters.value.promptType
    if (filters.value.dateFrom)   params.date_from   = filters.value.dateFrom
    if (filters.value.dateTo)     params.date_to     = filters.value.dateTo
    if (filters.value.userSearch) params.user_search = filters.value.userSearch

    const { data } = await api.get('/ai/history/', { params })
    items.value      = data.results || []
    totalCount.value = data.count   || 0
  } catch (e) {
    console.error('AI history fetch error:', e)
  } finally {
    loading.value = false
  }
}

function applyFilters() {
  currentPage.value = 1
  selectedItem.value = null
  fetchHistory()
}

function clearFilters() {
  filters.value = { promptType: '', dateFrom: '', dateTo: '', userSearch: '' }
  currentPage.value = 1
  selectedItem.value = null
  fetchHistory()
}

function goPage(p) {
  if (p < 1 || p > totalPages.value) return
  currentPage.value = p
  fetchHistory()
}

function openItem(item) {
  selectedItem.value = item
  showSnapshot.value = false
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function printItem() {
  const content = document.querySelector('.detail-body')?.innerHTML || ''
  const win = window.open('', '_blank')
  win.document.write(`
    <html><head><title>AI Report</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 2rem; line-height: 1.6; }
      table { border-collapse: collapse; width: 100%; margin: 1rem 0; }
      th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; }
      th { background: #f5f5f5; font-weight: 600; }
      h1, h2, h3 { color: #1a1a2e; margin-top: 1.5rem; }
    </style></head>
    <body>${content}</body></html>`)
  win.document.close()
  win.print()
}

function getPromptLabel(key) {
  return prompts.find(p => p.key === key)?.label ?? key
}

function getBadgeClass(key) {
  if (key === 'ethiopian-govt-report') return 'badge-govt'
  if (key === 'govt-audit-ethiopia')   return 'badge-audit'
  return 'badge-default'
}

function formatDate(d) {
  return new Date(d).toLocaleString('en-ET', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  })
}

onMounted(fetchHistory)
</script>

<style scoped>
/* ── Page layout ── */
.ai-history-page {
  padding: 1.5rem;
  min-height: 100%;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.page-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-icon { font-size: 1.3rem; }
.page-subtitle { font-size: 0.875rem; }

/* ── Two-column layout ── */
.history-layout {
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
}

/* ── Filter panel ── */
.filter-panel {
  flex: 0 0 240px;
  position: sticky;
  top: 1rem;
}

.filter-card {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 6px rgba(0,0,0,.08);
  border: 1px solid #e9ecef;
}

.filter-title {
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
  color: #1a1a2e;
}

.filter-group {
  margin-bottom: 0.9rem;
}

.filter-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.3rem;
}

.filter-actions {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
}

/* active filter tags */
.active-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.filter-tag-label {
  font-size: 0.7rem;
  color: #6c757d;
  width: 100%;
  font-weight: 600;
}
.filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: #e7f3ff;
  color: #0d6efd;
  border-radius: 20px;
  font-size: 0.7rem;
  padding: 0.15rem 0.5rem;
  font-weight: 500;
}
.filter-tag button {
  background: none;
  border: none;
  color: #0d6efd;
  cursor: pointer;
  padding: 0;
  font-size: 0.85rem;
  line-height: 1;
}

/* stats card */
.stats-card {
  background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  color: #fff;
  display: flex;
  justify-content: space-around;
}
.stat-item {
  text-align: center;
}
.stat-number {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
}
.stat-label {
  font-size: 0.7rem;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ── Results panel ── */
.results-panel {
  flex: 1;
  min-width: 0;
}

.results-count {
  font-size: 0.82rem;
  color: #6c757d;
  font-weight: 500;
}

/* ── Table ── */
.table-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0,0,0,.08);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.history-table thead tr {
  background: #f8fafc;
  border-bottom: 2px solid #e9ecef;
}

.history-table th {
  padding: 0.75rem 1rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6c757d;
  white-space: nowrap;
}

.table-row {
  border-bottom: 1px solid #f0f4f8;
  cursor: pointer;
  transition: background 0.15s;
}
.table-row:last-child { border-bottom: none; }
.table-row:hover { background: #f0f7ff; }

.history-table td {
  padding: 0.75rem 1rem;
  vertical-align: middle;
}

.row-num  { color: #adb5bd; font-size: 0.78rem; width: 40px; }
.row-date { color: #495057; white-space: nowrap; }
.row-user { color: #495057; }

.user-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.82rem;
}
.user-chip i { color: #6c757d; }

/* Prompt badges */
.prompt-badge {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.3em 0.65em;
  border-radius: 20px;
}
.badge-govt    { background: #fffbeb; color: #b45309; border: 1px solid #fcd34d; }
.badge-audit   { background: #ecfdf5; color: #065f46; border: 1px solid #6ee7b7; }
.badge-default { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }

.btn-view {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  font-size: 0.78rem;
  padding: 0.25rem 0.65rem;
  border-radius: 6px;
  transition: all 0.15s;
  white-space: nowrap;
}
.btn-view:hover {
  background: #1d4ed8;
  color: #fff;
  border-color: #1d4ed8;
}

/* ── Pagination ── */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}
.page-numbers {
  display: flex;
  gap: 0.3rem;
}
.page-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.page-btn:hover { background: #e7f3ff; border-color: #0d6efd; color: #0d6efd; }
.page-btn.active { background: #0d6efd; border-color: #0d6efd; color: #fff; font-weight: 700; }

/* ── Detail card ── */
.detail-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0,0,0,.08);
  border: 1px solid #e9ecef;
  padding: 1.5rem;
}
.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;
}
.detail-header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.detail-meta {
  font-size: 0.8rem;
  color: #6c757d;
}
.detail-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

/* ── Answer body (markdown) ── */
.answer-body {
  font-size: 0.95rem;
  line-height: 1.7;
  color: #212529;
}
.answer-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 0.85rem;
}
.answer-body :deep(th),
.answer-body :deep(td) {
  padding: 0.5rem 0.75rem;
  border: 1px solid #dee2e6;
  text-align: left;
}
.answer-body :deep(th) {
  background: #f8f9fa;
  font-weight: 600;
}
.answer-body :deep(h1),
.answer-body :deep(h2),
.answer-body :deep(h3) {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  color: #1a1a2e;
}
.answer-body :deep(hr) {
  border: none;
  border-top: 1px solid #e9ecef;
  margin: 1.5rem 0;
}

/* ── Raw data ── */
.raw-data {
  margin-top: 1rem;
  background: #0f172a;
  color: #94a3b8;
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.75rem;
  overflow-x: auto;
  max-height: 400px;
}

/* ── Loading / Empty ── */
.loading-state {
  text-align: center;
  padding: 4rem 1rem;
  color: #6c757d;
}
.empty-state {
  text-align: center;
  padding: 4rem 1rem;
  background: #fff;
  border-radius: 12px;
  border: 2px dashed #dee2e6;
  color: #6c757d;
}
.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

/* ── Transitions ── */
.slide-in-enter-active { animation: slideIn 0.25s ease; }
.slide-in-leave-active { animation: slideIn 0.2s ease reverse; }
@keyframes slideIn {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ── Responsive ── */
@media (max-width: 767px) {
  .history-layout { flex-direction: column; }
  .filter-panel { flex: none; width: 100%; position: static; }
}
</style>
